// Le composeur — la boîte « Que Dieu a-t-il mis sur votre cœur aujourd'hui ? ».
//
// Il vivait dans l'écran du fil, où il est né. Il en est sorti le jour où le
// PROFIL a dû publier lui aussi : deux écrans qui se partagent une boîte de
// deux cent soixante lignes, ce sont deux boîtes qui divergent. Celle-ci n'a
// qu'un exemplaire, et les deux écrans l'importent — comme `ComposeurEspace`
// le fait déjà pour les groupes et les pages.
//
// Ce qu'il sait faire : le texte, les mentions, le type (publication, prière,
// témoignage, verset), les photos en album ou la vidéo — jamais les deux à la
// fois — et la limite de taille selon le mode.
//
// `ref` expose `remplir()` : c'est ainsi que la carte du verset du jour dépose
// son texte dans la boîte, depuis l'écran du fil.

import { useImperativeHandle, useRef, useState } from "react";
import Store from "../store.js";
import { APP } from "../config.js";
import { toast } from "../ui.js";
import Avatar from "./Avatar.jsx";
import Icone from "./Icone.jsx";
import { useMentions, ListeMentions } from "./Mentions.jsx";

/* ⚠️ CES DEUX-LÀ SONT VENUES AVEC LE COMPOSEUR, ET ELLES ONT FAILLI RESTER
   DERRIÈRE. Elles étaient déclarées en haut de `Fil.jsx`, hors du bloc déplacé.
   Le bloc est parti sans elles, `Fil.jsx` les a perdues au ménage, et plus
   personne ne les déclarait : le composeur levait une ReferenceError au premier
   rendu — donc l'accueil ET le profil affichaient un écran BLANC.

   Rien ne l'avait signalé. La compilation ne voit pas une variable absente, et
   le banc d'essai éprouve les règles du serveur, pas ce que le navigateur
   dessine. La leçon est de déplacer un bloc AVEC ce dont il se sert, et de
   toujours ouvrir l'écran après un déplacement — un `✓ built` ne prouve rien
   de ce qui s'affiche. */

// Dix photos : au-delà, personne ne fait défiler, et l'envoi devient long sur
// une connexion mobile.
const MAX_PHOTOS = 10;

const INVITES = {
  priere: "Partagez votre sujet de prière…",
  temoignage: "Racontez ce que Dieu a fait…",
  verset: "Un verset qui vous a parlé…",
  post: "Que Dieu a-t-il mis sur votre cœur aujourd'hui ?",
};

export function ComposeurBloque({ moi }) {
  const [envoi, setEnvoi] = useState(false);

  /* L'e-mail de confirmation part d'un domaine partagé par tous les projets
     Firebase : Gmail ne peut pas vérifier qu'il vient de Koinonia, et le range
     souvent dans les indésirables. Tant que Koinonia n'a pas son propre domaine
     signé, le seul remède est de le DIRE — un membre qui ne trouve pas le
     message croit que l'inscription a échoué, et ne revient pas. */
  const renvoyer = async () => {
    setEnvoi(true);
    try {
      await Store.auth.envoyerVerification();
      toast("Lien renvoyé. Pensez à regarder dans les indésirables.");
    } catch (e) {
      toast(e.message || "Envoi impossible", "erreur");
    }
    setEnvoi(false);
  };

  if (moi?.emailVerifie === false)
    return (
      <section className="composeur composeur--bloque composeur--email">
        <Icone nom="alertes" taille={20} />
        <div>
          <p>Confirmez votre adresse e-mail pour publier votre première publication.</p>
          <p className="petit texte-doux">
            Le message n'est pas arrivé ? <strong>Regardez dans vos courriers indésirables</strong>{" "}
            (ou l'onglet « Promotions » sur Gmail), puis marquez-le comme légitime : les suivants
            arriveront directement.
          </p>
          <button className="btn btn--fin" type="button" onClick={renvoyer} disabled={envoi}>
            {envoi ? "Envoi…" : "Renvoyer le lien"}
          </button>
        </div>
      </section>
    );
  return (
    <section className="composeur composeur--bloque">
      <Icone nom="options" taille={20} />
      <p>Votre compte est suspendu : vous ne pouvez pas publier pour le moment.</p>
    </section>
  );
}

export default function Composeur({ ctx, ref }) {
  const [texte, setTexte] = useState("");
  const [type, setType] = useState("post");
  // { genre: "image", urls: [...] } ou { genre: "video", urls: [une] }
  // Une publication porte SOIT des photos, SOIT une vidéo : une vidéo au milieu
  // d'un album ne se regarde pas, et l'album ne se feuillette plus.
  const [media, setMedia] = useState(null);
  const [info, setInfo] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const zone = useRef(null);
  const mentions = useMentions(texte, setTexte, ctx);

  // Le champ grandit avec le texte, jusqu'à un plafond.
  const ajuster = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(260, el.scrollHeight) + "px";
  };

  // « Partager ce verset » remplit le champ d'ici. C'est le seul geste qu'un
  // autre bout de l'écran demande au composeur, donc on n'expose que celui-là —
  // et par le mécanisme prévu, jamais en écrivant dans une référence pendant le
  // rendu, ce qui serait un effet de bord au mauvais moment.
  useImperativeHandle(
    ref,
    () => ({
      remplir(v) {
        setTexte(`« ${v.t} »\n— ${v.r}`);
        setType("verset");
        zone.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        zone.current?.focus();
      },
    }),
    []
  );

  const limiteVideo = Store.mode === "local" ? APP.videoMaxMo.local : APP.videoMaxMo.firebase;

  const choisir = async (fichiers, genre) => {
    const liste = [...(fichiers || [])].filter((f) => f.type.startsWith(genre + "/"));
    if (!liste.length)
      return toast(genre === "image" ? "Ce fichier n'est pas une image" : "Ce fichier n'est pas une vidéo", "erreur");
    if (genre === "video" && liste[0].size > limiteVideo * 1024 * 1024)
      return toast(
        `Vidéo trop lourde (max ${limiteVideo} Mo${Store.mode === "local" ? " en mode démo" : ""})`,
        "erreur"
      );

    // Une vidéo reste seule ; les photos s'ajoutent à celles déjà choisies.
    const dejaLa = genre === "image" && media?.genre === "image" ? media.urls : [];
    const aFaire = genre === "video" ? liste.slice(0, 1) : liste.slice(0, MAX_PHOTOS - dejaLa.length);
    if (!aFaire.length) return toast(`${MAX_PHOTOS} photos au maximum`, "erreur");

    setInfo(
      genre === "video"
        ? "Préparation de la vidéo…"
        : `Préparation de ${aFaire.length > 1 ? aFaire.length + " photos" : "la photo"}…`
    );
    try {
      // En série et non en parallèle : sur une 4G, dix envois simultanés se
      // gênent et finissent plus tard que dix envois à la suite.
      //
      // ⚠️ Et l'on COMPTE À VOIX HAUTE. Chaque fichier demande au serveur un
      // jeton de dépôt, délivré au compte-gouttes ; entre deux photos, le
      // pilote patiente quelques secondes plutôt que d'échouer. Sans ce
      // décompte, cette attente n'est qu'un écran figé — et un écran figé, on
      // le referme. « Envoi 2 sur 3… » change une panne apparente en travail
      // visible.
      const urls = [...dejaLa];
      for (const [i, f] of aFaire.entries()) {
        if (aFaire.length > 1) setInfo(`Envoi ${i + 1} sur ${aFaire.length}…`);
        urls.push(await Store.upload(f, genre));
      }
      setMedia({ genre, urls });
    } catch (e) {
      toast(e.message || "Fichier impossible à charger", "erreur");
    }
    setInfo("");
  };

  const retirerPhoto = (i) =>
    setMedia((m) => {
      const urls = m.urls.filter((_, k) => k !== i);
      return urls.length ? { ...m, urls } : null;
    });

  const prete = Boolean(texte.trim() || media);

  const publier = async () => {
    if (!prete || envoi) return;
    setEnvoi(true);
    setInfo("Publication…");
    const citees = mentions.mentions();
    try {
      await Store.posts.add({
        type,
        authorId: ctx.moi.uid,
        auteurNom: ctx.moi.nom,
        auteurPhoto: ctx.moi.photo || "",
        texte: texte.trim(),
        mentions: mentions.mentions(),
        // `image` garde la PREMIÈRE photo : tout ce qui a été écrit avant les
        // albums la lit encore, et rien de l'ancien n'a besoin d'être réécrit.
        image: media?.genre === "image" ? media.urls[0] : "",
        images: media?.genre === "image" ? media.urls : [],
        video: media?.genre === "video" ? media.urls[0] : "",
      });
      // prévenir ceux qu'on a cités — c'est tout l'intérêt de les citer
      for (const m of citees)
        Store.notifs.add(m.uid, {
          type: "mention",
          deId: ctx.moi.uid,
          deNom: ctx.moi.nom,
          dePhoto: ctx.moi.photo || "",
          apercu: texte.trim().slice(0, 80),
        });
      setTexte("");
      setMedia(null);
      mentions.oublier();
      ajuster(zone.current);
      toast("Publié 🙌");
    } catch (e) {
      toast(e.message || "Publication impossible", "erreur");
    }
    setInfo("");
    setEnvoi(false);
  };

  return (
    <section className="composeur">
      <div className="composeur__haut">
        <Avatar membre={ctx.moi} taille={44} />
        <textarea
          ref={zone}
          rows={1}
          maxLength={3000}
          placeholder={INVITES[type] || INVITES.post}
          value={texte}
          onChange={(e) => {
            setTexte(e.target.value);
            ajuster(e.target);
            mentions.surSaisie(e.target);
          }}
        />
      </div>
      <ListeMentions
        propositions={mentions.propositions}
        onChoisir={(u) => mentions.choisir(u, zone.current)}
      />

      {media && (
        <div className={`composeur__apercu${media.urls.length > 1 ? " composeur__apercu--album" : ""}`}>
          {media.genre === "video" ? (
            <>
              <video src={media.urls[0]} controls playsInline />
              <span className="composeur__retirer" onClick={() => setMedia(null)}>
                ✕ Retirer
              </span>
            </>
          ) : (
            media.urls.map((u, i) => (
              <span className="composeur__vignette" key={u}>
                <img src={u} alt="" />
                {/* chaque photo se retire seule : sinon il faut tout refaire
                    pour en enlever une */}
                <button
                  type="button"
                  className="composeur__retirer composeur__retirer--vignette"
                  aria-label={`Retirer la photo ${i + 1}`}
                  onClick={() => retirerPhoto(i)}
                >
                  ✕
                </button>
              </span>
            ))
          )}
        </div>
      )}

      <div className="composeur__types">
        {APP.typesPost.map((t) => (
          <button
            key={t.id}
            className={`chip chip--petit${t.id === type ? " est-actif" : ""}`}
            onClick={() => {
              setType(t.id);
              zone.current?.focus();
            }}
          >
            {t.icone} {t.label}
          </button>
        ))}
      </div>

      <div className="composeur__bas">
        <label className="btn btn--fantome btn--petit">
          <Icone nom="photo" taille={19} /> Photos
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              choisir(e.target.files, "image");
              e.target.value = "";
            }}
          />
        </label>
        <label className="btn btn--fantome btn--petit">
          <Icone nom="video" taille={19} /> Vidéo
          <input
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => {
              choisir(e.target.files, "video");
              e.target.value = "";
            }}
          />
        </label>
        <span className="composeur__info">{info}</span>
        <button className="btn btn--primaire" disabled={!prete || envoi} onClick={publier}>
          Publier
        </button>
      </div>
    </section>
  );
}
